import { useEffect, useRef, useState } from "react";
import { useDashboardStatusBarColor } from "web_myplann/Dashboard/Hooks/useDashboardStatusBarColor";
import { DASHBOARD_TRANSITION_DURATION_MS } from "web_myplann/Dashboard/Utils/dashboardLayout";
import type { DashboardType } from "../Components/DashboardTitleCard";

export type AnimationDirection = "left" | "right" | null;

export const useDashboardAnimation = (
	dashboards: DashboardType[],
	defaultValue: number,
	enabled = true,
	isCardVisible = true,
) => {
	const [isAnimating, setIsAnimating] = useState(false);
	const [animationDirection, setAnimationDirection] =
		useState<AnimationDirection>(null);
	const [selectedId, setSelectedId] = useState<number | undefined>(
		dashboards[defaultValue]?.id,
	);
	const [previousDashboard, setPreviousDashboard] =
		useState<DashboardType | null>(null);
	const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	const appliedDefaultValueRef = useRef<number | undefined>(undefined);

	const dashboard =
		dashboards.find((d) => d.id === selectedId) ?? dashboards[defaultValue];

	const activeIndex = Math.max(
		dashboards.findIndex((d) => d.id === dashboard?.id),
		0,
	);

	useEffect(() => {
		return () => {
			if (animationTimeoutRef.current) {
				clearTimeout(animationTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!enabled) {
			appliedDefaultValueRef.current = undefined;
			return;
		}

		const shouldApplyDefault = appliedDefaultValueRef.current !== defaultValue;
		appliedDefaultValueRef.current = defaultValue;

		setSelectedId((currentId) => {
			const currentSelectionExists = dashboards.some(
				(item) => item.id === currentId,
			);

			if (shouldApplyDefault || !currentSelectionExists) {
				return dashboards[defaultValue]?.id;
			}

			return currentId;
		});
	}, [dashboards, defaultValue, enabled]);

	useDashboardStatusBarColor(
		enabled && dashboard && isCardVisible ? dashboard.gradientFrom : undefined,
	);

	const handleDashboardChange = (
		newIndex: number,
		direction: AnimationDirection,
	) => {
		if (isAnimating) return;

		const newDashboard = dashboards[newIndex];
		if (newDashboard.id === dashboard?.id) return;

		setPreviousDashboard(dashboard);
		setAnimationDirection(direction);
		setIsAnimating(true);
		setSelectedId(newDashboard.id);

		if (animationTimeoutRef.current) {
			clearTimeout(animationTimeoutRef.current);
		}
		animationTimeoutRef.current = setTimeout(() => {
			setIsAnimating(false);
			setPreviousDashboard(null);
			setAnimationDirection(null);
			animationTimeoutRef.current = null;
		}, DASHBOARD_TRANSITION_DURATION_MS);
	};

	const calculateDirection = (
		currentDashboard: DashboardType,
		targetIndex: number,
	): AnimationDirection => {
		const currentIndex = dashboards.findIndex(
			(d) => d.id === currentDashboard.id,
		);
		return targetIndex > currentIndex ? "right" : "left";
	};

	return {
		dashboard,
		activeIndex,
		isAnimating,
		previousDashboard,
		animationDirection,
		handleDashboardChange,
		calculateDirection,
	};
};
